import { useState } from 'react'
import { JSBI, Pair, Percent } from '@magikswap/sdk'
import {
  Button,
  Text,
  ChevronUpIcon,
  ChevronDownIcon,
  Flex,
  CardProps,
  useModal,
  TooltipText,
  useMatchBreakpointsContext,
  useTooltip,
} from '@pancakeswap/uikit'
import { NextLinkFromReactRouter } from 'components/NextLink'
import { useTranslation } from '@pancakeswap/localization'
import { useWeb3React } from '@web3-react/core'
import { BIG_INT_ZERO } from 'config/constants/exchange'
import currencyId from 'utils/currencyId'
import { unwrappedToken } from 'utils/wrappedCurrency'
import { CurrencyLogo } from 'components/Logo'
import { DoubleCurrencyLogo } from 'components/Logo'
import { FaMinusCircle } from '@react-icons/all-files/fa/FaMinusCircle'
import { MdAddCircle } from '@react-icons/all-files/md/MdAddCircle'
import useBUSDPrice from 'hooks/useBUSDPrice'
import { multiplyPriceByAmount } from 'utils/prices'
import { useTokenBalance } from 'state/wallet/hooks'
import useTotalSupply from 'hooks/useTotalSupply'

import { PositionDetailsFlex, MainFlex, V2LiquidityCard, ShowMore } from '../styled'
import styled from 'styled-components'
import RingLoader from 'react-spinners/RingLoader'
import RemoveLiquidityModal from './RemoveLiquidityModal'

const LiquidityActionButton = styled(Button)`
  background: ${({ theme, gradient }) =>
    gradient ? theme.colors.gradients[gradient] : theme.colors.gradients.blueteal};
  border: 2px solid ${({ theme }) => theme.colors.text};
  color: ${({ iconColor }) => iconColor || 'text'};
`

interface PositionCardProps extends CardProps {
  pair: Pair
  showUnwrapped?: boolean
  onDismiss?: () => void
}

const useLPValues = (account, pair, currency0, currency1) => {
  const token0Price = useBUSDPrice(currency0)
  const token1Price = useBUSDPrice(currency1)

  const userPoolBalance = useTokenBalance(account ?? undefined, pair.liquidityToken)
  const totalPoolTokens = useTotalSupply(pair.liquidityToken)

  const poolTokenPercentage =
    !!userPoolBalance && !!totalPoolTokens && JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? new Percent(userPoolBalance.raw, totalPoolTokens.raw)
      : undefined

  const [token0Deposited, token1Deposited] =
    !!pair &&
    !!totalPoolTokens &&
    !!userPoolBalance &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? [
          pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
          pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false),
        ]
      : [undefined, undefined]

  const token0USDValue =
    token0Deposited && token0Price
      ? multiplyPriceByAmount(token0Price, parseFloat(token0Deposited.toSignificant(6)))
      : null
  const token1USDValue =
    token1Deposited && token1Price
      ? multiplyPriceByAmount(token1Price, parseFloat(token1Deposited.toSignificant(6)))
      : null
  const totalUSDValue = token0USDValue && token1USDValue ? token0USDValue + token1USDValue : null

  return { token0Deposited, token1Deposited, totalUSDValue, poolTokenPercentage, userPoolBalance }
}

export default function FullPositionCard({ pair, onDismiss, ...props }: PositionCardProps) {
  const { t } = useTranslation()
  const { isDesktop } = useMatchBreakpointsContext()
  const { account } = useWeb3React()
  const [showMore, setShowMore] = useState(false)
  const currency0 = unwrappedToken(pair.token0)
  const currency1 = unwrappedToken(pair.token1)

  const formatToFourDecimalPlaces = (num) => {
    return parseFloat(num).toFixed(4)
  }
  const { totalUSDValue, poolTokenPercentage, token0Deposited, token1Deposited, userPoolBalance } = useLPValues(
    account,
    pair,
    currency0,
    currency1,
  )
  const [onClickMinus] = useModal(
    <RemoveLiquidityModal
      currencyA={currency0}
      currencyB={currency1}
      onDismiss={onDismiss}
      totalUSDValue={totalUSDValue}
      poolTokenPercentage={poolTokenPercentage}
      tokenADeposited={token0Deposited}
      tokenBDeposited={token1Deposited}
      userPoolBalance={userPoolBalance}
    />,
  )

  return (
    <V2LiquidityCard {...props}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        role="button"
        height="100%"
        padding="0px"
        onClick={(e) => {
          if (e.target.closest('button')) {
            return
          }
          if (!isDesktop) {
            setShowMore(!showMore)
          }
        }}
      >
        <MainFlex flex={2} alignItems="center" justifyContent="flex-start">
          <DoubleCurrencyLogo currency0={currency0} currency1={currency1} />
          <Text ml="0px" fontSize={isDesktop ? '24px' : '20px'} marginLeft="8px" textAlign="center">
            {!currency0 || !currency1 ? (
              <RingLoader
                color="#37C0DF"
                // loading={loading}
                // cssOverride={override}
                size={15}
                aria-setsize={12}
                speedMultiplier={0.9}
                aria-label="Loading Spinner"
                data-testid="loader"
              />
            ) : (
              `${currency0.symbol}/${currency1.symbol}`
            )}
            <Text fontSize={isDesktop ? '16px' : '14px'} color="textSubtle">
              {formatToFourDecimalPlaces(userPoolBalance?.toSignificant(3))} LP SHARES
            </Text>
          </Text>
        </MainFlex>

        {isDesktop && (
          <Flex flex={3} justifyContent="flex-start" alignItems="center">
            <PositionDetailsFlex>
              <Flex justifyContent="center" alignItems="center">
                <CurrencyLogo size="24px" currency={currency0} />
                <Text fontSize={isDesktop ? '24px' : '16px'} fontWeight="500" color="text" ml="4px">
                  {t('%asset%', { asset: currency0.symbol })}: {token0Deposited?.toSignificant(3)}
                </Text>
              </Flex>
              <Flex justifyContent="center" alignItems="center">
                <CurrencyLogo size="20px" currency={currency1} />
                <Text fontSize={isDesktop ? '24px' : '12px'} fontWeight="500" color="text" ml="4px">
                  {t('%asset%', { asset: currency1.symbol })}: {token1Deposited?.toSignificant(3)}
                </Text>
              </Flex>
            </PositionDetailsFlex>
            <Flex justifyContent="flex-start" flex={1} alignItems="flex-start" flexDirection="column">
              <Flex alignItems="flex-start" justifyContent="flex-start">
                <Text color="teal" ml="10px" fontSize={isDesktop ? '24px' : '18px'}>
                  ~${totalUSDValue?.toFixed(2) || 0}
                </Text>
              </Flex>
              <Text fontSize={isDesktop ? '20px' : '12px'} color="textSubtle" fontWeight="500">
                {t('POOL SHARE: ')}&nbsp;{' '}
                {poolTokenPercentage
                  ? `${poolTokenPercentage.toFixed(2) === '0.00' ? '<0.01' : poolTokenPercentage.toFixed(2)}%`
                  : '-'}
              </Text>
            </Flex>
          </Flex>
        )}

        {userPoolBalance && JSBI.greaterThan(userPoolBalance.raw, BIG_INT_ZERO) && (
          <Flex flex={1} flexDirection="row" alignItems="center" justifyContent="flex-end">
            {/* <NextLinkFromReactRouter to={`/remove/${currencyId(currency0)}/${currencyId(currency1)}?step=1`}> */}
            <LiquidityActionButton
              gradient="blueteal"
              onClick={onClickMinus}
              iconColor="#131313"
              variant="secondarytrans"
              mb="0px"
              scale="sm"
              marginX={isDesktop ? '12px' : '4px'}
            >
              <FaMinusCircle />
            </LiquidityActionButton>
            {/* </NextLinkFromReactRouter> */}
            <NextLinkFromReactRouter to={`/add/${currencyId(currency0)}/${currencyId(currency1)}?step=1`}>
              <LiquidityActionButton
                gradient="blueteal"
                iconColor="#131313"
                variant="secondarytrans"
                mb="0px"
                scale="sm"
                marginX={isDesktop ? '12px' : '4px'}
              >
                <MdAddCircle />
              </LiquidityActionButton>
            </NextLinkFromReactRouter>
          </Flex>
        )}

        {userPoolBalance && JSBI.greaterThan(userPoolBalance.raw, BIG_INT_ZERO) && !isDesktop && (
          <Flex>{showMore ? <ChevronUpIcon width="24px" /> : <ChevronDownIcon width="24px" />}</Flex>
        )}
      </Flex>

      {showMore && (
        <ShowMore>
          <Flex padding="4px" paddingX="1rem" flexDirection="row" justifyContent="space-between">
            <Flex>
              <CurrencyLogo size="20px" currency={currency0} />
              <Text fontSize="14px" fontWeight="500" color="text" ml="4px">
                {t('%asset% in LP', { asset: currency0.symbol })}:
              </Text>
            </Flex>
            <Flex>
              {token0Deposited ? (
                <Flex>
                  <Text fontSize="14px" fontWeight="500" color="text" ml="4px">
                    {token0Deposited?.toSignificant(4)}
                  </Text>
                </Flex>
              ) : (
                '-'
              )}
            </Flex>
          </Flex>

          <Flex padding="0px" paddingX="1rem" flexDirection="row" justifyContent="space-between">
            <Flex>
              <CurrencyLogo size="20px" currency={currency1} />
              <Text fontSize="14px" fontWeight="500" color="text" ml="4px">
                {t('%asset% in LP', { asset: currency1.symbol })}:
              </Text>
            </Flex>
            {token1Deposited ? (
              <Flex>
                <Text fontSize="14px" fontWeight="500" color="text" ml="4px">
                  {token1Deposited?.toSignificant(4)}
                </Text>
              </Flex>
            ) : (
              '-'
            )}
          </Flex>
          <Flex flexDirection="row" paddingX="1rem" marginBottom="0px" alignItems="center" justifyContent="flex-start">
            <Text fontSize="12px" color="#eee" fontWeight="500">
              {t('SHARE OF POOL: ')}
              {poolTokenPercentage
                ? `${poolTokenPercentage.toFixed(2) === '0.00' ? '<0.01' : poolTokenPercentage.toFixed(2)}%`
                : '-'}
            </Text>
          </Flex>
        </ShowMore>
      )}
    </V2LiquidityCard>
  )
}
